import Vue from 'vue'
import VueRouter from 'vue-router'

// Routes
import cubusRoutes from '@/cubus/route/cubus_routes'
import useJwt from '@/cubus/jwt/useJwt'
import store from '@/store/index'
// COMMENTED OUT FOR BUILD PERFORMANCE - DEMO ROUTES NOT USED
// import apps from './routes/apps'
// import dashboard from './routes/dashboard'
// import uiElements from './routes/ui-elements/index'
import pages from './routes/pages'
// import chartsMaps from './routes/charts-maps'
// import formsTable from './routes/forms-tables'
// import others from './routes/others'

Vue.use(VueRouter)

const CHUNK_RELOAD_KEY = 'cubus-chunk-reload'
const MAX_RELOAD_ATTEMPTS = 3

const isChunkLoadError = error => /ChunkLoadError/.test(error.name)
  || /Loading chunk \d+ failed/.test(error.message)
  || /Loading CSS chunk \d+ failed/.test(error.message)
  || /Failed to fetch dynamically imported module/.test(error.message)

const showChunkLoadFailure = () => {
  const el = document.createElement('div')
  el.id = 'chunk-load-error'
  el.innerHTML = `
    <div style="position:fixed;top:0;left:0;right:0;bottom:0;background:rgba(0,0,0,.65);z-index:99999;display:flex;align-items:center;justify-content:center">
      <div style="background:#fff;padding:32px 40px;border-radius:10px;text-align:center;max-width:360px;box-shadow:0 8px 32px rgba(0,0,0,.2)">
        <div style="font-size:36px;margin-bottom:12px">⚠️</div>
        <h3 style="margin:0 0 10px;font-size:18px;color:#333">Ошибка загрузки</h3>
        <p style="margin:0 0 24px;font-size:14px;color:#666;line-height:1.5">
          Приложение было обновлено.<br>Пожалуйста, обновите страницу.
        </p>
        <button
          onclick="sessionStorage.removeItem('${CHUNK_RELOAD_KEY}');window.location.reload()"
          style="background:#7367f0;color:#fff;border:none;padding:12px 28px;border-radius:6px;cursor:pointer;font-size:14px;font-weight:500">
          Обновить страницу
        </button>
      </div>
    </div>`
  document.body.appendChild(el)
}

const reloadAfterClearingSW = () => {
  const doReload = () => window.location.reload()
  if ('serviceWorker' in navigator) {
    Promise.all([
      navigator.serviceWorker.getRegistrations()
        .then(regs => Promise.all(regs.map(r => r.unregister()))),
      typeof caches !== 'undefined'
        ? caches.keys().then(names => Promise.all(names.map(n => caches.delete(n))))
        : Promise.resolve(),
    ]).then(doReload, doReload)
  } else {
    doReload()
  }
}

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  scrollBehavior() {
    return { x: 0, y: 0 }
  },
  routes: [
    { path: '/', redirect: { name: 'dashboard-cubus' } },
    // COMMENTED OUT FOR BUILD PERFORMANCE - DEMO ROUTES NOT USED
    // ...apps,
    // ...dashboard,
    ...pages,
    // ...chartsMaps,
    // ...formsTable,
    // ...uiElements,
    // ...others,

    // Cubus Dashboard - restored from demo routes
    {
      path: '/dashboard/cubus',
      name: 'dashboard-cubus',
      component: () => import('@/cubus/dashboard/CubusDashboard.vue'),
    },

    ...cubusRoutes,
    {
      path: '*',
      redirect: 'error-404',
    },
  ],
})

router.beforeEach((to, _, next) => {
  console.log('before each', to)
  if (to.name === 'auth-login') return next()
  if (to.name === 'auth-register') return next()
  if (to.name === 'auth-forgot-password') return next()
  if (to.name === 'auth-forgot-password-set-new') return next()
  // PROP-086: гвард больше НЕ смотрит на токен в localStorage — токена там нет.
  // Внутри SPA сессия лежит в памяти (Vuex); при холодной загрузке/перезагрузке память пуста,
  // и сессия восстанавливается запросом whoami БЕЗ токена в теле — аутентифицирует httpOnly-cookie
  // (whoami на бэке резолвит токен из cookie). Нет cookie/сессии — уходим на логин.
  const sess = store.getters['cubus-store/SESSION']
  if (sess && sess.user) return next()
  useJwt.whoami({})
    .then(response => {
      if (response.data && response.data.session) {
        store.commit('cubus-store/SET_SESSION', response.data.session)
        return next()
      }
      return next({ name: 'auth-login' })
    })
    .catch(error => {
      if (isChunkLoadError(error)) {
        // Let router.onError handle ChunkLoadErrors from navigation; just stop the guard here.
        return undefined
      }
      return next({ name: 'auth-login' })
    })
  // NOTE: intentionally NOT calling next() synchronously here.
  // Returning without calling next() keeps the navigation pending until whoami resolves.
  return undefined
})

// ? For splash screen
// Remove afterEach hook if you are not using splash screen
router.afterEach(() => {
  // Remove initial loading
  const appLoading = document.getElementById('loading-bg')
  if (appLoading) {
    appLoading.style.display = 'none'
  }
  // Successful navigation — reset the reload attempt counter
  sessionStorage.removeItem(CHUNK_RELOAD_KEY)
  router._chunkReloadScheduled = false
  /*
  Vue.nextTick(() => {
    document.title = to.meta.title ? `Cubus - ${to.meta.title}` : 'Cubus - Business Automation Platform'
  })
  */
})

// When a lazy-loaded chunk is missing (e.g. after a new deployment while the SW serves stale cache),
// unregister the service worker, clear all caches, then reload.
// Counter guard (max MAX_RELOAD_ATTEMPTS per session) prevents infinite reload loops.
// Counter resets after any successful navigation (router.afterEach).
router.onError(error => {
  if (!isChunkLoadError(error)) return
  // Multiple router.onError calls may fire for the same navigation (webpack retries + double next()).
  // Debounce: ignore if a reload is already scheduled in this tick.
  if (router._chunkReloadScheduled) return
  const attempts = parseInt(sessionStorage.getItem(CHUNK_RELOAD_KEY) || '0', 10)
  if (attempts >= MAX_RELOAD_ATTEMPTS) {
    console.error(`ChunkLoadError: ${attempts} reload attempts failed — showing error UI:`, error.message)
    showChunkLoadFailure()
    return
  }
  router._chunkReloadScheduled = true
  console.warn(`ChunkLoadError: attempt ${attempts + 1}/${MAX_RELOAD_ATTEMPTS}, clearing SW cache and reloading:`, error.message)
  sessionStorage.setItem(CHUNK_RELOAD_KEY, String(attempts + 1))
  reloadAfterClearingSW()
})

export default router
